/**
 * Request management for front end.
 * @see https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest/responseType
 */

import { DEFAULT_ERROR_MESSAGE, HEADER_ACCESS_KEY, responseCodes, SOURCE } from './const'
import { RequestOptions, Response } from '@/types/requests-types'
import debug from '@/helpers/debug'
import { NOOP } from '@/utils'
import ResponseError from './response-error'
import store from '@/store'


let HOST: string
if (API_SERVER) {
    HOST = API_SERVER
} else {
    // Enable lvh.me by running: `host lvh.me`.
    HOST = 'http://lvh.me:10001'
}


function createXHRRequestObject(method: string, urlPath: string, options: RequestOptions): XMLHttpRequest {
    const xhr = new XMLHttpRequest()
    xhr.open(method, `${HOST}${urlPath}`)
    xhr.setRequestHeader('Content-Type', 'application/json;charset=UTF-8')
    if (options.authenticated && typeof store.state.authKey === 'string') {
        xhr.setRequestHeader(HEADER_ACCESS_KEY, store.state.authKey)
    }
    return xhr
}


/**
 * Create an http request.
 * @param {string} method
 * @param {string} urlPath
 * @param {object} options
 * @return {Promise<object>}
 * @throws ResponseError
 * @private
 * @async
 */
function _httpRequest(method: string, urlPath: string, options: RequestOptions): Promise<Response> {
    return new Promise((resolve, reject) => {
        try {
            const xhr = createXHRRequestObject(method, urlPath, options)
            xhr.onreadystatechange = () => {
                try {
                    if (xhr.readyState === 4) {
                        if (xhr.status === 200) {
                            // Required to support IE 11
                            const response = JSON.parse(xhr.response) as Response
                            if (options.returnRaw) {
                                resolve(response)
                            } else {
                                if (response.status === responseCodes.SUCCESS) {
                                    resolve(response)
                                } else {
                                    response.message = response.message || DEFAULT_ERROR_MESSAGE
                                    debug.info(`Error status in response from ${urlPath} ${response}`)
                                    reject(ResponseError.fromResponse(response))
                                }
                            }
                        } else if (xhr.status === 401) {
                            const response = JSON.parse(xhr.response)
                            store.dispatch('clearAuthToken', response.message).then(NOOP)
                            reject(response.message)
                        } else {
                            // Non 200 response code from our server. This is invalid behavior
                            debug.error(`Invalid response code from url: ${urlPath}`, xhr.response)
                            reject(ResponseError.fromUnhandled())
                        }
                    }
                } catch(e) {
                    debug.error(`Error in 'onreadystatechange': ${e}`)
                    reject(ResponseError.fromUnhandled())
                }
            }
            xhr.send(JSON.stringify(options.data || {}))
        } catch(e) {
            debug.error(`HTTP ${method} request failed: ${e}`)
            reject(ResponseError.fromUnhandled())
        }
    })
}

/**
 * @param url
 * @param data
 * @param [authenticated=true]: Add authentication headers to this request.
 * @return {Promise<Response>}
 */
function post(url: string, data = {}, authenticated = true): Promise<Response> {
    return _httpRequest('POST', url, {
        authenticated,
        data,
        returnRaw: false
    })
}

/**
 * @param url
 * @param data
 * @param [authenticated=false]: Add authentication headers to this request.
 * @return {Promise<object>}
 */
function rawPost(url: string, data = {}, authenticated = false) {
    return _httpRequest('POST', url, {
        authenticated,
        data,
        returnRaw: true
    })
}


export default {
    post,
    rawPost
}
export { responseCodes, ResponseError, DEFAULT_ERROR_MESSAGE, SOURCE, Response }
